/**
 * Painel lateral da previsão (desktop): a opacidade e os ajustes da variável
 * no mapa, à esquerda, colado ao trilho — o mesmo lugar e o mesmo desenho do
 * painel das camadas observadas (ui/Sidebar), pedido do usuário de 21/10/2026.
 * Antes eles abriam no cartão da direita a cada entrada no "Próximos dias".
 *
 * Abre pela seta do botão da previsão no trilho (BotoesDaPrevisao) e pelo
 * botão de ajustes do cabeçalho da ficha; fecha pelo "recolher", pela mesma
 * seta e ao sair do modo. Um painel lateral por vez: abrir este recolhe o das
 * observadas, e abrir o das observadas recolhe este (App). O das observadas,
 * se estava aberto, volta ao sair do modo (ele fica guardado no navegador).
 * Ao abrir, o foco vai para o painel; ao recolher, volta à seta da variável.
 * No celular os ajustes continuam na folha, pelo "⚙" do trilho.
 */
import { PanelLeftClose } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { SIDEBAR_WIDTH } from '../map/viewControl';
import { useUiStore } from '../state/ui';
import { IconButton } from '../ui/primitives';
import { AjustesDaPrevisao } from './AjustesDaPrevisao';
import { ICONES, ROTULOS } from './BotoesDaPrevisao';
import { aoMudarModo, usePrevisaoStore } from './store';

/** O painel das observadas estava aberto quando este o recolheu (volta ao sair do modo). */
let observadasAbertasAntes = false;

/** Id do painel (para o `aria-controls` das setas do trilho). */
export const ID_PAINEL_LATERAL = 'painel-lateral-previsao';

/** Abre ou recolhe o painel lateral da previsão (recolhendo o das observadas). */
export function abrirPainelLateral(on: boolean): void {
  if (on && useUiStore.getState().sidebarOpen) {
    observadasAbertasAntes = true;
    useUiStore.getState().set({ sidebarOpen: false });
  }
  if (!on && usePrevisaoStore.getState().lateral) {
    // O foco volta à seta da variável (o botão focado some com o painel).
    const v = usePrevisaoStore.getState().variavel;
    queueMicrotask(() => document.querySelector<HTMLElement>(`[data-rail-previsao="${v}"] [data-seta-previsao]`)?.focus());
  }
  usePrevisaoStore.getState().setLateral(on);
}

/**
 * Entrar na previsão pela aba ou pela barra recolhe o painel das observadas
 * (ele mostraria os ajustes de uma camada que saiu do mapa); ele volta ao sair.
 */
export function recolherObservadasAoEntrar(): void {
  if (!useUiStore.getState().sidebarOpen) return;
  observadasAbertasAntes = true;
  useUiStore.getState().set({ sidebarOpen: false });
}

/** Ao sair do modo, devolve o painel das observadas que este recolheu. Devolve a função de desligar. */
export function iniciarPainelLateral(): () => void {
  const pararModo = aoMudarModo((modo) => {
    if (modo) return;
    // "Voltar ao observado" de dentro do painel: o botão focado some com ele; o foco vai à aba.
    queueMicrotask(() => {
      if (document.activeElement === document.body) document.querySelector<HTMLElement>('[data-aba-previsao]')?.focus();
    });
    if (!observadasAbertasAntes) return;
    observadasAbertasAntes = false;
    if (!useUiStore.getState().sidebarOpen) useUiStore.getState().set({ sidebarOpen: true });
  });
  // A pessoa reabriu o das observadas durante a previsão: dali em diante a escolha é dela.
  const pararUi = useUiStore.subscribe((a, b) => {
    if (a.sidebarOpen && !b.sidebarOpen) observadasAbertasAntes = false;
  });
  return () => {
    pararModo();
    pararUi();
  };
}

export function PainelLateralDaPrevisao() {
  const modo = usePrevisaoStore((s) => s.modo);
  const lateral = usePrevisaoStore((s) => s.lateral);
  const variavel = usePrevisaoStore((s) => s.variavel);
  const ref = useRef<HTMLElement>(null);
  const aberto = modo && lateral;
  useEffect(() => {
    if (aberto) ref.current?.focus({ preventScroll: true });
  }, [aberto]);
  if (!aberto) return null;
  const Icon = ICONES[variavel];
  return (
    <aside
      ref={ref}
      id={ID_PAINEL_LATERAL}
      tabIndex={-1}
      // O mesmo marcador do painel das observadas: o mapa, as fichas e o ponto da previsão desviam dele.
      data-painel-lateral=""
      data-painel-lateral-previsao=""
      className="absolute inset-y-0 left-0 z-30 flex min-h-0 flex-col border-r border-ink-700/80 bg-ink-900 outline-none shadow-[8px_0_24px_-12px_rgba(0,0,0,0.6)]"
      style={{ width: SIDEBAR_WIDTH }}
      aria-label="Painel da previsão"
      onKeyDown={(e) => {
        if (e.key === 'Escape') abrirPainelLateral(false);
      }}
    >
      <div className="flex items-center gap-2 border-b border-ink-700/70 px-3 py-2">
        <Icon className="size-4 shrink-0 text-violet-300" aria-hidden />
        <h2 className="min-w-0 flex-1 truncate text-sm font-semibold text-white">
          {ROTULOS[variavel]} <span className="font-normal text-violet-300">· previsão</span>
        </h2>
        <IconButton icon={PanelLeftClose} label="Recolher painel" size="sm" onClick={() => abrirPainelLateral(false)} />
      </div>
      <div className="scroll-thin min-h-0 flex-1 overflow-y-auto">
        <AjustesDaPrevisao />
      </div>
    </aside>
  );
}
